import QuestionIcon from '@mui/icons-material/HelpOutline'
import { DashboardCard, SectionTitle } from '../../../../../ui'

const SummaryHelp = ({ message, CustomTooltip }) => (
  <CustomTooltip title={message} arrow>
    <span className='ml-1 inline-flex cursor-help items-center text-sm leading-none text-muted transition-colors hover:text-text'>
      <QuestionIcon fontSize='inherit' />
    </span>
  </CustomTooltip>
)

const ExecutiveSummaryView = ({
  currentMonthYearLabel,
  executiveAccent,
  executiveScore,
  executiveStatus,
  totalSaldoAcumulado,
  shouldCompactValues,
  parseDisplayNumber,
  formatCLPFull,
  formatCLPDisplay,
  totales,
  runwayMonths,
  incomeDeltaPct,
  formatDecimalDisplay,
  formatBaseDisplayValue,
  volatilityLabel,
  providerSummary,
  topProviderAccounts,
  executiveScoreMeaning,
  executiveGoodBullet,
  executiveImportantBullet,
  executiveNextStepBullet,
  executiveFactors,
  executiveFactorExplainers,
  CustomTooltip,
  kpiNumberClass
}) => (
  <div className='overflow-hidden rounded-card border border-border bg-surface shadow-card'>
    <div className='border-b border-border px-4 py-4 sm:px-6 sm:py-5'>
      <div className='flex flex-col gap-1 lg:flex-row lg:items-center lg:gap-4'>
        <h2 className='text-[clamp(1.5rem,3vw,2.2rem)] font-bold text-text'>Resumen ejecutivo</h2>
        <p className='text-sm sm:text-base text-muted'>
          Posicion de la empresa al dia de hoy | {currentMonthYearLabel}
        </p>
      </div>
    </div>

    <div className='flex flex-col gap-6 p-4 sm:p-6 lg:flex-row lg:items-start'>
      <div className='flex min-w-0 flex-1 flex-col gap-6'>
        <div className='min-w-0 rounded-card border border-border bg-border/20 p-5 sm:p-6'>
          <h3 className='text-xl font-semibold text-text'>Estado general</h3>
          <p className='mt-2 max-w-4xl text-xs sm:text-sm leading-relaxed text-muted'>
            Mira este bloque como una foto rapida del negocio: cuanto margen tienes hoy, cuanto efectivo hay y donde conviene mirar primero.
          </p>

          <div className='mt-4 grid grid-cols-1 gap-4 items-stretch lg:grid-cols-[260px_minmax(0,1fr)]'>
            <div className='flex w-full h-full self-stretch min-w-0 items-center justify-center rounded-card border border-border bg-surface p-4 sm:p-6'>
              <div className='flex h-full w-full flex-col items-center justify-center text-center'>
                <div className='flex items-center justify-center text-sm sm:text-base font-semibold text-text'>
                  Estado general
                  <SummaryHelp
                    message='Tomamos senales simples: si estas ganando o perdiendo, si entra o sale mas dinero, cuanto queda por cobrar y cuanto tiempo puedes seguir con la caja disponible.'
                    CustomTooltip={CustomTooltip}
                  />
                </div>
                <p className='mt-2 text-xs sm:text-sm leading-relaxed text-muted'>
                  Este puntaje resume la salud del negocio hoy. Mientras mas alto, mas comodo estas para operar.
                </p>
                <div className='relative h-[98px] w-[176px] overflow-hidden sm:h-[112px] sm:w-[200px]'>
                  <div
                    className='absolute left-1/2 top-0 h-[176px] w-[176px] -translate-x-1/2 rounded-full sm:h-[200px] sm:w-[200px]'
                    style={{
                      background: `conic-gradient(from 180deg, ${executiveAccent} 0deg ${Math.max(
                        12,
                        executiveScore * 1.8
                      )}deg, rgb(var(--border)) ${Math.max(12, executiveScore * 1.8)}deg 180deg, transparent 180deg 360deg)`,
                    }}
                  />
                  <div
                    className='absolute left-1/2 top-4 h-[128px] w-[128px] -translate-x-1/2 rounded-full bg-surface sm:top-5 sm:h-[144px] sm:w-[144px]'
                  />
                  <div className='absolute inset-0 flex flex-col items-center justify-center pt-3 sm:pt-4'>
                    <div className='flex items-end gap-1 leading-none'>
                      <div className='text-[clamp(1.55rem,2.4vw,2.3rem)] font-bold text-text'>{executiveScore}</div>
                      <div className='pb-1 text-[10px] sm:text-[11px] font-semibold text-muted'>/100</div>
                    </div>
                    <div
                      className='text-xs sm:text-sm font-semibold'
                      style={{ color: executiveAccent }}
                    >
                      {executiveStatus}
                    </div>
                  </div>
                </div>
                <div className='mt-3 text-xs sm:text-sm leading-relaxed text-muted'>
                  Traduccion: vas bien si entra mas dinero del que sale.
                </div>
              </div>
            </div>

            <div className='grid w-full min-w-0 grid-cols-1 gap-4 auto-rows-fr items-stretch md:grid-cols-2'>
              <div className='flex w-full h-auto min-w-0 flex-col rounded-card border border-border bg-surface p-4 md:h-full md:min-h-[120px] lg:min-h-[140px]'>
                <div className='flex items-center gap-1'>
                  <div className='inline-flex rounded-full bg-[#F4E5B6] px-4 py-1 text-sm font-semibold text-text'>
                    Lo que va ganando
                  </div>
                  <SummaryHelp
                    message='Si es positivo, el negocio genera resultado. Si es negativo, estas gastando mas de lo que entra.'
                    CustomTooltip={CustomTooltip}
                  />
                </div>
                <div
                  className={`mt-3 min-w-0 text-[clamp(1rem,1.8vw,1.5rem)] font-bold ${
                    totalSaldoAcumulado >= 0 ? 'text-text' : 'text-danger'
                  } ${kpiNumberClass}`}
                  title={shouldCompactValues && Math.abs(parseDisplayNumber(totalSaldoAcumulado)) >= 1_000_000 ? formatCLPFull(totalSaldoAcumulado) : undefined}
                >
                  {`${totalSaldoAcumulado >= 0 ? '+' : ''}${formatCLPDisplay(totalSaldoAcumulado)}`}
                </div>
                <div className='mt-1 text-sm text-muted'>Ganancia acumulada del negocio</div>
                <div className='mt-2 text-xs sm:text-sm leading-relaxed text-muted'>
                  Es lo que queda despues de restar gastos a ingresos en el periodo.
                </div>
              </div>

              <div className='flex w-full h-auto min-w-0 flex-col rounded-card border border-border bg-surface p-4 md:h-full md:min-h-[120px] lg:min-h-[140px]'>
                <div className='flex items-center gap-1'>
                  <div className='inline-flex rounded-full bg-[#F5DCDD] px-4 py-1 text-sm font-semibold text-text'>
                    Plata disponible
                  </div>
                  <SummaryHelp
                    message='Ejemplo: 0,47 meses equivale a cerca de 14 dias si sigues gastando al mismo ritmo.'
                    CustomTooltip={CustomTooltip}
                  />
                </div>
                <div className='mt-3 flex flex-wrap items-end gap-4'>
                  <div className='min-w-0'>
                    <div
                      className={`min-w-0 text-[clamp(0.95rem,1.6vw,1.35rem)] font-bold text-text ${kpiNumberClass}`}
                      title={shouldCompactValues && Math.abs(parseDisplayNumber(totales?.diferencia)) >= 1_000_000 ? formatCLPFull(totales?.diferencia) : undefined}
                    >
                      {formatCLPDisplay(totales?.diferencia)}
                    </div>
                    <div className='mt-1 text-sm text-muted'>Dinero disponible hoy</div>
                  </div>
                  <div className='min-w-0 border-l border-border pl-4'>
                    <div className='text-lg font-semibold text-text'>{formatBaseDisplayValue(runwayMonths)} meses</div>
                    <div className='mt-1 text-sm text-muted'>Alcanza para operar</div>
                  </div>
                </div>
                <div className='mt-2 text-xs sm:text-sm leading-relaxed text-muted'>
                  Tu &quot;colchon&quot;: cuanta caja tienes y cuanto tiempo alcanza si mantienes el ritmo de gastos actual.
                </div>
              </div>

              <div className='flex w-full h-auto min-w-0 flex-col rounded-card border border-border bg-surface p-4 md:h-full md:min-h-[120px] lg:min-h-[140px]'>
                <div className='flex items-center gap-1'>
                  <div className='text-[1.05rem] font-semibold text-text'>Como se esta moviendo</div>
                </div>
                <div className='mt-3 flex flex-wrap items-center gap-4'>
                  <div className='min-w-0'>
                    <div className='flex items-center text-sm text-muted'>
                      Ingresos de este mes
                      <SummaryHelp
                        message='Si baja, puede ser estacionalidad o una alerta temprana: revisa ventas y cobranza.'
                        CustomTooltip={CustomTooltip}
                      />
                    </div>
                    <div className={`text-base font-semibold ${incomeDeltaPct >= 0 ? 'text-primary' : 'text-danger'}`}>
                      {`${incomeDeltaPct >= 0 ? '+' : ''}${formatDecimalDisplay(incomeDeltaPct)}%`}
                    </div>
                  </div>
                  <div className='min-w-0 border-l border-border pl-4'>
                    <div className='text-sm text-muted'>Movimiento</div>
                    <div className='text-base font-semibold text-text'>{volatilityLabel}</div>
                  </div>
                </div>
                <div className='mt-2 text-xs sm:text-sm leading-relaxed text-muted'>
                  Compara este mes con tu ritmo normal (promedio de 3 meses).
                </div>
              </div>

              <div className='flex w-full h-auto min-w-0 flex-col rounded-card border border-border bg-surface p-4 md:h-full md:min-h-[120px] lg:min-h-[140px]'>
                <div className='flex items-center gap-1'>
                  <div className='text-[1.05rem] font-semibold text-text'>Pagos pendientes</div>
                  <SummaryHelp
                    message='Si aparece 0 y sabes que tienes pagos, puede faltar registro o estar en otra seccion.'
                    CustomTooltip={CustomTooltip}
                  />
                </div>
                <div className='mt-3 text-sm text-muted'>
                  Hoy hay pagos por hacer por{' '}
                  <span className={`font-semibold text-text ${kpiNumberClass}`}>
                    {formatCLPDisplay(providerSummary.montoTotalGeneral)}
                  </span>
                  .
                </div>
                <div className='mt-2 text-xs sm:text-sm text-muted'>
                  {topProviderAccounts[0]?.name ? `Pago mas grande: ${topProviderAccounts[0].name}` : 'Aun no hay pagos cargados para resumir'}
                </div>
                <div className='mt-2 text-xs sm:text-sm leading-relaxed text-muted'>
                  Son pagos registrados como &quot;por hacer&quot;. No son todos los gastos del mes.
                </div>
              </div>
            </div>
          </div>

          <div className='mt-4 rounded-card border border-border bg-surface px-4 py-3 text-sm leading-relaxed text-muted'>
            <span className='font-semibold text-text'>{executiveScore}/100</span> quiere decir: {executiveScoreMeaning}
          </div>
        </div>

        <DashboardCard className='min-w-0'>
          <div className='flex flex-col gap-3'>
            <SectionTitle className='text-left'>Que esta pasando</SectionTitle>
            <ul className='space-y-3'>
              <li className='text-sm sm:text-base leading-relaxed text-muted'>
                <span className='font-semibold text-text'>Lo bueno:</span> {executiveGoodBullet}
              </li>
              <li className='text-sm sm:text-base leading-relaxed text-muted'>
                <span className='font-semibold text-text'>Lo importante:</span> {executiveImportantBullet}
              </li>
              <li className='text-sm sm:text-base leading-relaxed text-muted'>
                <span className='font-semibold text-text'>Siguiente paso:</span> {executiveNextStepBullet}
              </li>
            </ul>
          </div>
        </DashboardCard>
      </div>

      <div className='w-full shrink-0 self-start lg:sticky lg:top-6 lg:w-[320px] xl:w-[360px]'>
        <DashboardCard className='min-w-0'>
          <div className='flex flex-col'>
            <SectionTitle className='text-left'>Principales factores</SectionTitle>

            <div className='mt-4 flex flex-col divide-y divide-border'>
              {executiveFactors.map(item => (
                <div key={item.title} className='py-4 first:pt-0 last:pb-0'>
                  <div className='flex min-w-0 flex-col gap-1'>
                    <div className='min-w-0'>
                      <div className='text-base font-semibold text-text'>{item.title}</div>
                      <div className={`mt-1 text-sm ${item.toneClass}`}>{item.detail}</div>
                    </div>
                    <div className={`text-base font-semibold ${item.toneClass} break-words`}>
                      {item.value}
                    </div>
                    {executiveFactorExplainers[item.title] && (
                      <div className='text-xs sm:text-sm leading-relaxed text-muted'>
                        {executiveFactorExplainers[item.title]}
                      </div>
                    )}
                  </div>
                  <div className='mt-3'>
                    <span className='inline-flex rounded-lg bg-border px-3 py-2 text-sm text-muted'>
                      {item.action}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className='mt-6 border-t border-border pt-4'>
              <div className='text-sm font-semibold text-muted'>Acciones links</div>
              <div className='mt-3 flex flex-col gap-2 text-sm text-text'>
                <span>Revisar top CxC vencidas</span>
                <span>Ver gastos fuera de patron</span>
              </div>
            </div>
          </div>
        </DashboardCard>
      </div>
    </div>
  </div>
)

export default ExecutiveSummaryView
